import {
  BaseSyntheticEvent,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldErrors, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MoreVert from '@mui/icons-material/MoreVert';
import SimpleBreadcrumbsPage from '../../../../shared/simple-breadcrumbs-page';
import SimpleFormPageNew from '../../../../shared/simple-form-page-new';
import SharedButton from '../../../../shared/buttons/shared-button';
import ScopesAlert from '../../../../shared/scopes-alert';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import StatusLabel from '../../../../shared/status-label';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import { StepConfig } from '../../../../shared/shared-form-stepper-new';
import { pagesConfig } from '../../../../shared/pages-config';
import { ModuleList } from '../../../../shared/analytics/module-list';
import NotFoundPage from '../../../../shared/not-found-page';
import { useStore } from '../../../state/zustand-store';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { handleErrors } from '../../../../utils/errors';
import { Messages } from '../../../../utils/message-constants';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import {
  getAllDriveLabelsForManagerQuery,
  getDriveLabelLocksQuery,
} from '../../../../api/users-api';
import {
  DriveLabel,
  driveLabelsWriteAccessQuery,
  getDriveLabelForEditQuery,
  getPublishedDriveLabelQuery,
  publishDriveLabelEdit,
  saveDriveLabelEdit,
} from '../../../../api/drive-labels-api';
import StepLabelInformation from '../create-label/step-label-information';
import StepAddFields from '../create-label/step-add-fields';
import StepRules from '../create-label/step-rules';
import LabelPermissionsDialog from '../create-label/label-permissions-dialog';
import LabelPreviewPanel from '../create-label/label-preview-panel';
import WarningCallout from '../create-label/warning-callout';
import {
  fromGoogleLabel,
  toDraftModel,
} from '../create-label/label-draft-mapper';
import {
  CreateLabelForm,
  createLabelSchema,
  getCreateLabelDefaultValues,
  labelFieldsSchema,
  labelInformationSchema,
} from '../create-label/label-schema';
import { getLabelStatusText } from '../label-lifecycle';
import { getLockIndex } from '../label-locks';
import { useLabelActions } from '../use-label-actions';

const STEPS: StepConfig[] = [
  { label: 'Label Information', schema: labelInformationSchema },
  { label: 'Add Fields', schema: labelFieldsSchema },
  { label: 'Rules' },
];

const INFORMATION_FIELDS = Object.keys(
  labelInformationSchema.shape
) as (keyof CreateLabelForm)[];

const LABEL_MANAGER_PATH = `/${pagesConfig.driveLabelsManager.link}`;

export const COPY_MODE_NOTE =
  'Changes to this setting apply right away and do not require publishing.';
export const RULES_BANNER =
  'This label is currently used in rules, so renaming the label or its components could impact those rules.';
export const SAVE_BEFORE_ENABLE = 'Save your changes before enabling the label';
export const LOCKED_DELETE_HINT =
  'This label is associated with rule(s) that locks it from changes';

type Lock = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2LabelLock;

const BREADCRUMBS = [
  { label: ModuleList.modules.administration.name },
  {
    label: pagesConfig.driveLabelsManager.name,
    path: pagesConfig.driveLabelsManager.link,
  },
];

const hasUnpublishedChanges = (label: DriveLabel) =>
  !!label.lifecycle?.hasUnpublishedChanges;
const isNeverPublished = (label: DriveLabel) =>
  label.lifecycle?.state === 'UNPUBLISHED_DRAFT';
const isDisabledLabel = (label: DriveLabel) =>
  label.lifecycle?.state === 'DISABLED';

/**
 * Shows Edit Label only when Drive Labels and label operations are enabled for the domain, the same
 * gate as Create Label. The route itself only checks role and tier.
 */
export default function EditLabelPage() {
  const { features } = useUserSettings();
  if (
    !isFeatureEnabled('driveLabelsRead', features) ||
    !isFeatureEnabled('driveLabelsQ4', features)
  ) {
    return <NotFoundPage />;
  }
  return <EditLabelLoader />;
}

function PageMessage({ children }: { children: React.ReactNode }) {
  return (
    <SimpleBreadcrumbsPage isSubPage breadcrumbs={BREADCRUMBS}>
      {children}
    </SimpleBreadcrumbsPage>
  );
}

/** Loads the label (and its locks, already cached when coming from Label Manager) before editing. */
function EditLabelLoader() {
  const { labelId = '' } = useParams<{ labelId: string }>();
  const { data: hasWriteAccess, isLoading: checkingWriteAccess } = useQuery(
    driveLabelsWriteAccessQuery()
  );
  const labelQuery = useQuery({
    ...getDriveLabelForEditQuery(labelId),
    enabled: !!labelId && !!hasWriteAccess,
  });
  const locksQuery = useQuery({
    ...getDriveLabelLocksQuery(labelId),
    enabled: !!labelId && !!hasWriteAccess,
  });

  if (checkingWriteAccess) {
    return (
      <PageMessage>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageMessage>
    );
  }
  if (!hasWriteAccess) {
    return (
      <PageMessage>
        <ScopesAlert
          apiKey="driveLabelsWrite"
          additionalText="Labels can't be edited until access is granted."
        />
      </PageMessage>
    );
  }
  if (labelQuery.isError) {
    return <NotFoundPage />;
  }
  if (!labelQuery.data || locksQuery.isLoading) {
    return (
      <PageMessage>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageMessage>
    );
  }

  return (
    <EditLabelForm
      key={labelId}
      labelId={labelId}
      label={labelQuery.data}
      locks={locksQuery.data}
    />
  );
}

interface EditLabelFormProps {
  labelId: string;
  label: DriveLabel;
  /** The label's Google rule locks, from the cache Label Manager already filled. */
  locks: Lock[] | undefined;
}

/** The header's Locked chip from the design: a grey pill with a lock icon. */
function LockedChip() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        height: 28,
        px: 1,
        borderRadius: '100px',
        bgcolor: (theme) =>
          theme.palette.mode === 'light' ? 'grey.100' : 'action.hover',
        color: 'text.secondary',
      }}
    >
      <LockOutlined sx={{ fontSize: 14 }} />
      <Typography variant="body2" sx={{ px: 0.75, letterSpacing: '0.17px' }}>
        Locked
      </Typography>
    </Stack>
  );
}

interface EditLabelAction {
  key: string;
  label: string;
  icon: ReactNode;
  /** Why the item is greyed out; enabled when empty. */
  blockedReason?: string;
  onClick: () => void;
}

/** The ⋮ in the header, as in the design: Clone on every label, and Delete for drafts and disabled labels. */
function EditLabelActionsMenu({ actions }: { actions: EditLabelAction[] }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip arrow title="Actions">
        <IconButton
          aria-label="Label actions"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {actions.map((action) => {
          const item = (
            <MenuItem
              key={action.key}
              disabled={!!action.blockedReason}
              onClick={() => {
                setAnchorEl(null);
                action.onClick();
              }}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              {action.label}
            </MenuItem>
          );
          // A greyed-out item ignores the pointer, so its reason sits on a wrapper.
          return action.blockedReason ? (
            <Tooltip
              key={action.key}
              arrow
              placement="left"
              title={action.blockedReason}
            >
              <span>{item}</span>
            </Tooltip>
          ) : (
            item
          );
        })}
      </Menu>
    </>
  );
}

function EditLabelForm({ labelId, label, locks }: EditLabelFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { changeLogRoles, primaryDomain, driveRoles } = useUserSettings();
  const lockIndex = useMemo(() => getLockIndex(locks), [locks]);

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<CreateLabelForm>({
    resolver: zodResolver(createLabelSchema),
    defaultValues: fromGoogleLabel(label),
    mode: 'onTouched',
  });
  const fieldArray = useFieldArray({
    control,
    name: 'fields',
    keyName: 'rhfKey',
  });

  // The label as Google last returned it; its lifecycle drives the status and the publish button.
  const [savedLabel, setSavedLabel] = useState<DriveLabel>(label);
  const showPublishedTab = hasUnpublishedChanges(savedLabel);
  const { data: publishedLabel } = useQuery(
    getPublishedDriveLabelQuery(labelId, showPublishedTab)
  );
  // A read-only copy of the published revision for the preview's Published tab.
  // Built once per loaded revision: fromGoogleLabel makes new client keys on every call, and
  // useForm resets whenever `values` changes, so building it on each render loops forever.
  const publishedValues = useMemo(
    () => (publishedLabel ? fromGoogleLabel(publishedLabel) : undefined),
    [publishedLabel]
  );
  const { control: publishedControl } = useForm<CreateLabelForm>({
    defaultValues: getCreateLabelDefaultValues(),
    values: publishedValues,
  });

  const [activeStep, setActiveStep] = useState(0);
  const [stepCompleted, setStepCompleted] = useState<boolean[]>(
    STEPS.map(() => false)
  );
  // A draft saved in an earlier visit shows its save time too, not only drafts saved on this page.
  const [lastSavedDate, setLastSavedDate] = useState(() =>
    hasUnpublishedChanges(label) || isNeverPublished(label)
      ? Date.parse(label.revisionCreateTime ?? '') || 0
      : 0
  );
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  // SimpleFormPageNew's Esc listener would discard the page while dnd-kit cancels a drag with Esc.
  const isDraggingRef = useRef(false);

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: confirmLeave,
    handleCancel: cancelLeave,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  // Label Manager's list already resolved the creator's email; reuse it instead of fetching again.
  // Opened straight from a link, the creator shows as empty until the list has loaded.
  const { data: managerLabels } = useQuery({
    ...getAllDriveLabelsForManagerQuery(),
    enabled: false,
  });
  const creator =
    managerLabels?.labels?.find((entry) => entry.label?.id === labelId)?.label
      ?.creator?.person ?? '';

  const requiresChangeLog = isAbleToSeeUIElement(
    changeLogRoles,
    DriveRoles.EditDriveLabels.value
  );
  const canDelete = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.DeleteDriveLabels.value
  );
  const canEnable = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.UpdateDriveLabelsStatus.value
  );

  // Enable and Delete reuse Label Manager's dialogs, change log and toasts.
  const labelActions = useLabelActions({
    changeLogRoles,
    onChanged: (_labels, action) => {
      if (action === 'delete') {
        bypass();
        navigate(LABEL_MANAGER_PATH);
        return;
      }
      // Enabling publishes the label: read its new state once, keeping any unsaved edits.
      void queryClient
        .fetchQuery({ ...getDriveLabelForEditQuery(labelId), staleTime: 0 })
        .then((fresh) => setSavedLabel(fresh))
        .catch((error) =>
          handleErrors({
            error,
            snackbarMessage: 'Failed to reload the label.',
          })
        );
    },
  });

  const applySavedLabel = (saved: DriveLabel) => {
    reset(fromGoogleLabel(saved, getValues()));
    setSavedLabel(saved);
    setLastSavedDate(Date.now());
    queryClient.setQueryData(
      getDriveLabelForEditQuery(labelId).queryKey,
      saved
    );
  };

  const saveMutation = useMutation({
    mutationFn: () => saveDriveLabelEdit(labelId, toDraftModel(getValues())),
    onSuccess: (saved) => {
      applySavedLabel(saved);
      // Label Manager's list now has a stale status, and copy mode already changed in Google.
      // Mark it stale without fetching it here; it reloads once Label Manager opens again.
      queryClient.invalidateQueries({
        queryKey: getAllDriveLabelsForManagerQuery().queryKey,
        exact: true,
        refetchType: 'none',
      });
      setSnackbar({ open: true, severity: 'success', message: 'Draft saved.' });
    },
    onError: (error) =>
      handleErrors({ error, snackbarMessage: 'Failed to save the draft.' }),
  });

  const publishMutation = useMutation({
    mutationFn: () =>
      publishDriveLabelEdit(
        labelId,
        toDraftModel(getValues()),
        changeLogMessage
      ),
    onSuccess: () => {
      setChangeLogMessage('');
      setChangeLogOpen(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Changes published.',
      });
      // Exact: the per-label lock and permission queries share this key as a prefix.
      queryClient.invalidateQueries({
        queryKey: getAllDriveLabelsForManagerQuery().queryKey,
        exact: true,
      });
      // The cached draft (and published revision) are now out of date; reopening Edit reads the
      // published label fresh instead of showing the old draft.
      queryClient.removeQueries({
        queryKey: getDriveLabelForEditQuery(labelId).queryKey,
      });
      bypass();
      navigate(LABEL_MANAGER_PATH);
    },
    onError: (error) =>
      handleErrors({
        error,
        snackbarMessage: 'Failed to publish the changes.',
      }),
  });

  const isBusy = saveMutation.isPending || publishMutation.isPending;
  const neverPublished = isNeverPublished(savedLabel);
  const disabledLabel = isDisabledLabel(savedLabel);
  // A saved draft can be published without new edits; otherwise there's nothing to publish.
  const canPublish = isDirty || neverPublished || showPublishedTab;
  // Like the design: with nothing waiting, the greyed button says the label is already live.
  const publishText = disabledLabel
    ? 'Enable Label'
    : neverPublished
    ? 'Publish'
    : canPublish
    ? 'Publish Changes'
    : 'Published';
  // A disabled label goes live through Enable, which publishes what Google has saved.
  const enableBlockedReason = !canEnable
    ? Messages.Errors.Permission.Acting(DriveRoles.UpdateDriveLabelsStatus.name)
    : isDirty
    ? SAVE_BEFORE_ENABLE
    : undefined;
  const submitDisabled = disabledLabel ? !!enableBlockedReason : !canPublish;
  // Figma: drafts and disabled labels can be deleted from here; published ones have no actions.
  const canClone = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CreateDriveLabels.value
  );
  const deleteBlockedReason = !canDelete
    ? Messages.Errors.Permission.Acting(DriveRoles.DeleteDriveLabels.name)
    : lockIndex.labelLocked
    ? LOCKED_DELETE_HINT
    : labelActions.isDeleting
    ? Messages.DriveLabels.DeleteInProgress
    : undefined;
  // "Last published" for a published label with nothing waiting; "Last saved" once there's a draft.
  const hasDraft = neverPublished || showPublishedTab || lastSavedDate > 0;
  const publishedDate =
    !hasDraft && !disabledLabel
      ? Date.parse(savedLabel.publishTime ?? '') || 0
      : 0;
  const lockedFieldNames = (getValues('fields') ?? [])
    .filter((field) => lockIndex.lockedFieldIds.has(field.id))
    .map((field) => field.name);

  const completeStep = (step: number) =>
    setStepCompleted((prev) =>
      prev.map((done, i) => (i === step ? true : done))
    );

  const showInvalid = (formErrors: FieldErrors<CreateLabelForm>) => {
    const infoHasError = INFORMATION_FIELDS.some((key) => formErrors[key]);
    setActiveStep(infoHasError ? 0 : 1);
    setSnackbar({
      open: true,
      severity: 'error',
      message: 'Fix the highlighted fields and try again.',
    });
  };

  const handleNext = async (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const valid =
      activeStep === 0
        ? await trigger(INFORMATION_FIELDS)
        : await trigger('fields');
    if (!valid) {
      return;
    }
    completeStep(activeStep);
    setActiveStep((step) => step + 1);
  };

  const handleSaveDraft = handleSubmit(
    () => saveMutation.mutate(),
    showInvalid
  );

  const viewRules = () => setActiveStep(STEPS.length - 1);

  const handleEnable = () => {
    if (!enableBlockedReason) {
      labelActions.requestEnable([savedLabel]);
    }
  };

  const handlePublish = handleSubmit(() => {
    if (disabledLabel) {
      handleEnable();
      return;
    }
    if (requiresChangeLog) {
      setChangeLogOpen(true);
    } else {
      publishMutation.mutate();
    }
  }, showInvalid);

  // Leaving drops only unsaved edits; a saved draft stays in Google until it's published.
  const handleCancel = () => {
    if (isDraggingRef.current) {
      return;
    }
    if (isDirty) {
      navigate(LABEL_MANAGER_PATH, { state: { fromCancelButton: true } });
      return;
    }
    bypass();
    navigate(LABEL_MANAGER_PATH);
  };

  const setDragging = (dragging: boolean) => {
    isDraggingRef.current = dragging;
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepLabelInformation
            control={control}
            errors={errors}
            isLabelCreated
            copyModeNote={COPY_MODE_NOTE}
          />
        );
      case 1:
        return (
          <StepAddFields
            control={control}
            errors={errors}
            fieldArray={fieldArray}
            onDragStateChange={setDragging}
            locks={lockIndex}
            onViewRules={viewRules}
          />
        );
      default:
        return (
          <StepRules
            usedInRule={lockIndex.hasLocks}
            lockedFieldNames={lockedFieldNames}
            labelId={labelId}
          />
        );
    }
  };

  // Like the design: the rules banner sits above the form, outside it, on every step.
  const rulesBanner = lockIndex.hasLocks ? (
    // Like the design: lock icon, outlined, and View Rules at the far right in the same color.
    <WarningCallout bordered padding={2} icon={<LockOutlined />}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body2" sx={{ letterSpacing: '0.17px' }}>
          {RULES_BANNER}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          color="inherit"
          underline="always"
          onClick={viewRules}
          sx={{ flexShrink: 0 }}
        >
          View Rules
        </Link>
      </Stack>
    </WarningCallout>
  ) : undefined;

  const enableButton = (
    <SharedButton
      variant="primary"
      onClick={handleEnable}
      disabled={isBusy || !!enableBlockedReason}
    >
      Enable Label
    </SharedButton>
  );

  return (
    <SimpleBreadcrumbsPage isSubPage breadcrumbs={BREADCRUMBS}>
      <SimpleFormPageNew
        title={pagesConfig.editDriveLabel.name}
        trigger={trigger}
        steps={STEPS}
        content={renderStep()}
        contentBanner={rulesBanner}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        stepCompleted={stepCompleted}
        setStepCompleted={setStepCompleted}
        errors={errors}
        handleNext={handleNext}
        handleDiscard={handleCancel}
        handleSubmit={handlePublish}
        submitButtonText={publishText}
        isLoadingSubmit={publishMutation.isPending}
        // The same button is Continue on earlier steps, so only the last step waits for changes.
        isDisableSubmit={
          isBusy || (activeStep === STEPS.length - 1 && submitDisabled)
        }
        lastSavedDate={publishedDate || lastSavedDate}
        lastSavedLabel={publishedDate ? 'Last published' : undefined}
        // The draft marker shows once the label has changes waiting to be published.
        status={
          !disabledLabel && (neverPublished || showPublishedTab)
            ? 'Draft'
            : undefined
        }
        titleAdornment={
          <Stack direction="row" spacing={1} alignItems="center">
            {!neverPublished && (
              <StatusLabel status={disabledLabel ? 'DISABLED' : 'PUBLISHED'} />
            )}
            {lockIndex.hasLocks && <LockedChip />}
          </Stack>
        }
        headerActions={
          <Stack direction="row" spacing={1} alignItems="center">
            <SharedButton
              variant="secondary"
              onClick={handleCancel}
              disabled={isBusy}
            >
              Cancel
            </SharedButton>
            <SharedButton
              variant="secondary"
              onClick={handleSaveDraft}
              loading={saveMutation.isPending}
              disabled={isBusy || !isDirty}
            >
              Save Draft
            </SharedButton>
            {disabledLabel ? (
              enableBlockedReason ? (
                <Tooltip arrow title={enableBlockedReason}>
                  <span>{enableButton}</span>
                </Tooltip>
              ) : (
                enableButton
              )
            ) : (
              <SharedButton
                variant="primary"
                onClick={handlePublish}
                loading={publishMutation.isPending}
                disabled={isBusy || !canPublish}
              >
                {publishText}
              </SharedButton>
            )}
            <EditLabelActionsMenu
              actions={[
                {
                  key: 'clone',
                  label: 'Clone',
                  icon: <ContentCopy fontSize="small" />,
                  blockedReason: canClone
                    ? undefined
                    : Messages.Errors.Permission.Acting(
                        DriveRoles.CreateDriveLabels.name
                      ),
                  // Clone copies the label as Google has it; unsaved edits get the leave prompt.
                  onClick: () => labelActions.cloneLabel(savedLabel),
                },
                // Figma: published labels can't be deleted from here (they're disabled first).
                ...(neverPublished || disabledLabel
                  ? [
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: <Delete fontSize="small" />,
                        blockedReason: deleteBlockedReason,
                        onClick: () => labelActions.requestDelete([savedLabel]),
                      },
                    ]
                  : []),
              ]}
            />
          </Stack>
        }
        sidePanel={
          <LabelPreviewPanel
            control={control}
            labelId={labelId}
            creator={creator}
            lastModified={Date.parse(savedLabel.revisionCreateTime ?? '') || 0}
            organizationName={primaryDomain}
            onEditPermissions={() => setPermissionsOpen(true)}
            status={getLabelStatusText(savedLabel)}
            publishedControl={
              showPublishedTab && publishedLabel ? publishedControl : undefined
            }
          />
        }
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Label"
        pageMode="edit"
        open={showUnsavedChangesDialog}
        handleConfirm={confirmLeave}
        handleClose={cancelLeave}
      />

      <LabelPermissionsDialog
        open={permissionsOpen}
        handleClose={() => setPermissionsOpen(false)}
        labelId={labelId}
        pendingPermissions={[]}
        onPendingPermissionsChange={() => {}}
        organizationName={primaryDomain}
        requiresChangeLog={requiresChangeLog}
        forEdit
      />

      <ChangeLogDialog
        open={changeLogOpen}
        handleClose={() => setChangeLogOpen(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => publishMutation.mutate()}
        mutateLoading={publishMutation.isPending}
      />

      {labelActions.dialogs}
    </SimpleBreadcrumbsPage>
  );
}
